import { ShieldAlert, RefreshCw, Check, X, AlertOctagon } from "lucide-react";
import { api } from "@/lib/api";
import { useAzure } from "@/context/AzureContext";
import { Button } from "@/components/ui/button";
import { Table, TableHeader, TableBody, TableRow, TableHead, TableCell } from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";

const RISK_STYLE = { low: "bg-yellow-100 text-yellow-700", medium: "bg-orange-100 text-orange-700", high: "bg-red-100 text-red-700" };
const STATUS_STYLE = { atRisk: "bg-red-100 text-red-700", dismissed: "bg-muted text-muted-foreground", confirmedSafe: "bg-emerald-100 text-emerald-700", confirmedCompromised: "bg-red-600 text-white" };
const STATUS_LABEL = { atRisk: "At risk", dismissed: "Dismissed", confirmedSafe: "Confirmed safe", confirmedCompromised: "Confirmed compromised" };
const RISK_LABEL = {
  unfamiliarSignInProperties: "Unfamiliar sign-in properties",
  anonymousIPAddress: "Anonymous IP address",
  leakedCredentials: "Leaked credentials",
  malwareLinkedIP: "Malware linked IP",
};

export default function IdentityProtection() {
  const { state, run, refresh } = useAzure();
  const detections = state?.risk_detections || [];

  const remediate = (d, action) =>
    run(() => api.post("/azure/identity/risk/remediate", { detection_id: d.id, action }),
      { success: action === "confirm_compromised" ? "User confirmed compromised & blocked" : action === "dismiss" ? "Risk dismissed" : "Sign-in confirmed safe" });

  const atRisk = detections.filter((d) => d.status === "atRisk").length;

  return (
    <div data-testid="az-identity-protection">
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-4">
        <div className="border border-border rounded-lg bg-card p-4"><div className="text-xs uppercase tracking-widest text-muted-foreground flex items-center gap-1.5"><ShieldAlert className="h-4 w-4" /> At risk</div><div className="font-display font-bold text-2xl mt-1 text-red-600">{atRisk}</div></div>
        <div className="border border-border rounded-lg bg-card p-4"><div className="text-xs uppercase tracking-widest text-muted-foreground">High</div><div className="font-display font-bold text-2xl mt-1">{detections.filter((d) => d.risk_level === "high").length}</div></div>
        <div className="border border-border rounded-lg bg-card p-4"><div className="text-xs uppercase tracking-widest text-muted-foreground">Detections</div><div className="font-display font-bold text-2xl mt-1">{detections.length}</div></div>
        <div className="border border-border rounded-lg bg-card p-4"><div className="text-xs uppercase tracking-widest text-muted-foreground">Remediated</div><div className="font-display font-bold text-2xl mt-1 text-emerald-600">{detections.filter((d) => d.status !== "atRisk").length}</div></div>
      </div>

      <div className="flex items-center gap-2 border-b border-border pb-3 mb-3">
        <Button size="sm" variant="ghost" onClick={refresh} className="gap-2"><RefreshCw className="h-4 w-4" /> Refresh</Button>
        <span className="text-xs text-muted-foreground ml-auto">Confirming compromise blocks the account. Dismissing clears the risk.</span>
      </div>

      <div className="border border-border rounded-md overflow-hidden">
        <Table>
          <TableHeader><TableRow><TableHead>User</TableHead><TableHead>Risk</TableHead><TableHead>Detection</TableHead><TableHead>IP / Location</TableHead><TableHead>Status</TableHead><TableHead className="w-64">Remediate</TableHead></TableRow></TableHeader>
          <TableBody>
            {detections.map((d) => (
              <TableRow key={d.id} data-testid={`az-risk-row-${d.user_upn}`}>
                <TableCell className="font-medium">{d.user_name}<div className="text-xs text-muted-foreground">{d.user_upn}</div></TableCell>
                <TableCell><Badge className={`text-xs capitalize ${RISK_STYLE[d.risk_level]}`}>{d.risk_level}</Badge></TableCell>
                <TableCell className="text-xs">{RISK_LABEL[d.risk_type] || d.risk_type}</TableCell>
                <TableCell className="text-xs text-muted-foreground font-mono">{d.ip}<div>{d.location}</div></TableCell>
                <TableCell><Badge className={`text-xs ${STATUS_STYLE[d.status]}`}>{STATUS_LABEL[d.status]}</Badge></TableCell>
                <TableCell>
                  {d.status === "atRisk" ? (
                    <div className="flex items-center gap-1">
                      <Button size="sm" variant="outline" className="h-7 text-xs gap-1" data-testid={`az-risk-dismiss-${d.user_upn}`} onClick={() => remediate(d, "dismiss")}><Check className="h-3.5 w-3.5" /> Dismiss</Button>
                      <Button size="sm" variant="outline" className="h-7 text-xs gap-1 text-emerald-700" onClick={() => remediate(d, "confirm_safe")}>Safe</Button>
                      <Button size="sm" variant="outline" className="h-7 text-xs gap-1 text-red-600" data-testid={`az-risk-compromise-${d.user_upn}`} onClick={() => remediate(d, "confirm_compromised")}><AlertOctagon className="h-3.5 w-3.5" /> Compromised</Button>
                    </div>
                  ) : <span className="text-xs text-muted-foreground">Resolved</span>}
                </TableCell>
              </TableRow>
            ))}
            {detections.length === 0 && <TableRow><TableCell colSpan={6} className="text-center text-muted-foreground py-8">No risk detections.</TableCell></TableRow>}
          </TableBody>
        </Table>
      </div>
    </div>
  );
}
